import getBlogPosts from '@/api/getBlogPosts';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import strapiConstants from '@/constants/strapi.constants';
import { BlogPost as BlogPostType } from '@/types/Blog';
import getBlogPostByDocumentId from '@/api/getBlogPostByDocumentId';
import getAllBlogPosts from '@/api/getAllBlogPosts';

function useFetchBlogPosts(query: string, locale: string) {
  const blogsQuery = useQuery({
    queryKey: ['blogs', query, locale],
    queryFn: ({ queryKey }) => getBlogPosts(queryKey[1], queryKey[2]),
  });

  return blogsQuery;
}

function useFetchAllBlogPosts(locale: string) {
  const blogsQuery = useQuery({
    queryKey: ['blogs', 'all', locale],
    queryFn: ({ queryKey }) => getAllBlogPosts(queryKey[2] as string),
  });

  return blogsQuery;
}

function useFetchBlogPostInBlogPage(initialBlog: BlogPostType) {
  const [manuallyRefetch, setManuallyRefetch] = useState<boolean>(false);
  const router = useRouter();
  const blogQuery = useQuery({
    queryKey: ['blog', router.locale],
    queryFn: async ({ queryKey }) =>
      await getBlogPostByDocumentId(initialBlog.documentId, queryKey[1] as string),
    initialData: initialBlog,
  });

  useEffect(() => {
    if (blogQuery.data) {
      if (blogQuery.data.attributes.slug !== initialBlog.attributes.slug) {
        router.replace(`/blog/${blogQuery.data.attributes.slug}`, undefined, {
          shallow: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogQuery.data]);

  useEffect(() => {
    async function refetchBlog() {
      setManuallyRefetch(true);
      await blogQuery.refetch();
      setManuallyRefetch(false);
    }
    refetchBlog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.locale]);

  return {
    ...blogQuery,
    manuallyRefetch,
  };
}

function useFetchBlogPostsWithQuery() {
  const [textQuery, setTextQuery] = useState<string>('');
  const [query, setQuery] = useState<string | null>(null);
  const [loadingQuery, setLoadingQuery] = useState<boolean>(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null); // Timer para debounce
  const { locale = 'en' } = useRouter();

  const blogsQuery = useQuery({
    queryKey: ['blogs', query, locale],
    queryFn: ({ queryKey }) => getBlogPosts(queryKey[1] || '', queryKey[2] as string),
    enabled: !!query,
  });

  function handleBlogSearch(searchQuery: string | null) {
    setTextQuery(searchQuery || '');
    setLoadingQuery(true);

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const newTimer = setTimeout(() => {
      setQuery(searchQuery ? strapiConstants.queries.allBlogsWithSearch(searchQuery) : null);
      setLoadingQuery(false);
    }, 500);

    setDebounceTimer(newTimer);
  }

  return {
    ...blogsQuery,
    isLoading: loadingQuery || blogsQuery.isLoading,
    data: blogsQuery.data || [],
    blogSearch: textQuery,
    handleBlogSearch,
  };
}

export {
  useFetchBlogPosts,
  useFetchBlogPostsWithQuery,
  useFetchBlogPostInBlogPage,
  useFetchAllBlogPosts,
};
